import {ModalToolContainerService, WebmateToolConfig} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {AuditActivityId, AuditAutomationRunId, AuditId, ProjectId} from '@webmate-web-core/shared/types/ids';
import {AuditReportConfig} from '@webmate-web-core/shared/types/audit';
import {Component, Inject, OnInit} from '@angular/core';
import {
    AuditService,
    HydratedAuditTarget,
    StandardAuditAutomationConfig
} from '@webmate-web-core/shared/services/audit.service';
import {WellKnownArtifactTypes} from '@webmate-web-core/shared/types/artifact';
import {TOOL_CONTAINER_DATA} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.component';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {FormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {CreateAuditReportToolComponent, CreateAuditReportToolConfig} from '@webmate-web-core/projects/project/auditlab/audits/create-auditreport-tool.component';
import {PlanningMatrixSelectionToolComponent, PlanningMatrixToolConfig} from '@webmate-web-core/projects/project/auditlab/audits/planningmatrix-selection-tool.component';
import {MenuItem} from 'primeng/api';
import {forkJoin, of} from 'rxjs';
import {catchError, filter, startWith, switchMap, take} from 'rxjs/operators';
import {Tag, tagFromString} from '@webmate-web-core/shared/types/tag';
import {faSyncAlt, faExclamationCircle} from '@fortawesome/pro-solid-svg-icons';
import {AutomationPolicyConfig} from '@webmate-web-core/shared/types/browsersession';
import {AuditInfo} from '../../../../../workbench/shared/services/audit-session';
import {ReportTemplate} from '../../../../../report/types';
import {ReportService} from '@webmate-web-core/shared/services/report.service';
import {ReportExpr} from '../../../../../report/report_types';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {TestObject} from '@webmate-web-core/shared/types/testmgmt';

export class StartAuditAutomationToolConfig implements WebmateToolConfig {
    toolName = 'Start Audit Automation';
    scrollable = true;

    constructor(
        public projectId: ProjectId,
        public auditId: AuditId,
        public auditReportParameters: {[name: string]: ReportExpr} = {},
        public activitiesToAutomate?: AuditActivityId[],
        public createCallback?: (run: AuditAutomationRunId) => void
    ) {
    }
}

@Component({
    templateUrl: './start-auditautomation-tool.component.html',
    styleUrls: ['./start-auditautomation-tool.component.less'],
})
export class StartAuditAutomationToolComponent implements OnInit {
    projectId: ProjectId;

    selectedActivities: AuditActivityId[] = [];
    testObjectsWithoutActivity: string[] = [];
    testObjects: HydratedAuditTarget[] = [];
    plannedCustomObjects: string[] = [];
    allReportTemplates: ReportTemplate[] = [];
    reportTemplate: ReportTemplate;
    reportConfig: AuditReportConfig;
    starting: boolean = false;
    loadingAuditInfo: boolean = true;
    hasMobileTestObjects: boolean = false;
    hasWebTestObjects: boolean = true;

    constructor(
        @Inject(TOOL_CONTAINER_DATA) public data: StartAuditAutomationToolConfig,
        private webmateNotificationService: WmNotificationService,
        private formBuilder: FormBuilder,
        private auditService: AuditService,
        private toolService: ModalToolContainerService,
        private reportService: ReportService,
        private testService: TestService
    ) {
        this.projectId = data.projectId;
        this.reportService.getReportTemplatesForProject$(this.projectId).subscribe(templates => {
            this.allReportTemplates = templates;
            this.reportTemplate = this.allReportTemplates.find(template => template.id === 'b26b0878-e9ff-4ceb-a05e-241d0920f00f') || this.allReportTemplates[0];
            this.reportConfig = this.reportTemplate.structure;
        });
    }

    form: UntypedFormGroup;

    executionStatusOptions: MenuItem[] = [
        {label: 'Not started', value: 'notstarted'},
        {label: 'In progress', value: 'inprogress'},
        {label: 'Finished', value: 'finished'},
        {label: 'Waiting for review', value: 'waitingforreview'},
        {label: 'Skipped', value: 'skipped'},
        {label: 'Blocked', value: 'blocked'},
    ];

    contentAnalysisLanguageOptions = [
        {label: 'English', value: 'en'},
        {label: 'German', value: 'de'},
        {label: 'French', value: 'fr'},
    ];

    private buildForm(templateLang?: string): void {
        const matchedOption = this.contentAnalysisLanguageOptions.find(option => templateLang?.startsWith(option.value));
        const language = matchedOption ? matchedOption.value : 'en';

        this.form = this.formBuilder.group({
            auditId: new UntypedFormControl(this.data.auditId, [Validators.required]),
            fullAudit: new UntypedFormControl(!this.data.activitiesToAutomate, [Validators.required]),
            generateReport: new UntypedFormControl(false, [Validators.required]),
            activitiesToAutomate: new UntypedFormControl(this.data.activitiesToAutomate || []),
            associateFindingsWithAudit: new UntypedFormControl(false, [Validators.required]),
            autoSetEvaluationStatusForAssessmentTasks: new UntypedFormControl(true, [Validators.required]),
            setExecutionStatusForAutomatedActivities: new UntypedFormControl(true, [Validators.required]),
            executionStatusForAutomatedActivities: new UntypedFormControl('waitingforreview'),
            setLabelsForAutomatedActivities: new UntypedFormControl(true),
            labelsForAutomatedActivities: new UntypedFormControl([tagFromString('wasAutomated')]),
            setLabelsForAutomatedFindings: new UntypedFormControl(true),
            labelsForAutomatedFindings: new UntypedFormControl([tagFromString('fromAutomation')]),
            enableAiAnalysis: new UntypedFormControl(true, [Validators.required]),
            contentAnalysisLanguage: new UntypedFormControl(language, [Validators.required]),
            webAutomationPolicy: new UntypedFormControl(undefined as AutomationPolicyConfig | undefined),
            mobileAutomationPolicy: new UntypedFormControl(undefined as AutomationPolicyConfig | undefined),
        });
        this.selectedActivities = this.form.get('activitiesToAutomate')?.value || [];

        this.form.get('setExecutionStatusForAutomatedActivities')
            ?.valueChanges
            .pipe(startWith(true))
            .subscribe(checked => {
                const selectControl = this.form.get('executionStatusForAutomatedActivities');
                checked ? selectControl?.enable() : selectControl?.disable();
            });
    }

    ngOnInit() {
        this.auditService.getOrCreateAuditSession(this.data.auditId, false).getAuditInfo$()
            .pipe(
                filter((info: AuditInfo) => !!info.audit),
                take(1),
                switchMap(info => {
                    const auditAssessmentTasks = info.assessmentTasks || [];
                    this.testObjects = info.testObjects || [];

                    const hasTask = new Set(auditAssessmentTasks.map(task => task.testObjectId));

                    this.testObjectsWithoutActivity = this.testObjects
                        .filter(object => !hasTask.has(object.testObjectId))
                        .map(object => (object.target.name || 'Missing Name').replace(/(^https?:\/\/)|(\/$)/g, ''));

                    this.loadingAuditInfo = false;

                    const testObjectIds = this.testObjects.map(target => target.testObjectId);
                    const testObjects$ = testObjectIds.length > 0
                        ? this.testService.getTestObjects$(testObjectIds).pipe(take(1), catchError(() => of([] as TestObject[])))
                        : of([] as TestObject[]);
                    // Use Audit's template to get default Language
                    const template$ = info.audit?.template
                        ? this.auditService.getAuditTemplate$(info.audit.template).pipe(take(1), catchError(() => of(undefined)))
                        : of(undefined);

                    return forkJoin({template: template$, testObjects: testObjects$});
                })
            )
            .subscribe({
                next: ({template, testObjects}) => {
                    this.classifyTestObjectPlatforms(testObjects);
                    this.buildForm(template?.lang);
                },
                error: () => this.buildForm()
            });
    }

    private classifyTestObjectPlatforms(testObjects: TestObject[]): void {
        if (testObjects.length === 0) {
            this.hasMobileTestObjects = false;
            this.hasWebTestObjects = true;
            return;
        }
        this.hasMobileTestObjects = testObjects.some(testObject => testObject.generationSpec?.type === 'MobileAppGenerationSpec');
        this.hasWebTestObjects = testObjects.some(testObject => testObject.generationSpec?.type !== 'MobileAppGenerationSpec');
    }

    setLabelsForAutomatedActivities(labels: Tag[]) {
        this.form.patchValue({labelsForAutomatedActivities: labels});
    }

    setLabelsForAutomatedFindings(labels: Tag[]) {
        this.form.patchValue({labelsForAutomatedFindings: labels});
    }

    startAutomation() {
        this.starting = true;
        if (this.form.invalid) {
            console.error('Form is invalid:', this.form.errors);
            this.starting = false;
            return;
        }

        const automationConfig: StandardAuditAutomationConfig = {
            type: 'StandardAuditAutomationConfig',
            activities: this.form.value.fullAudit ? undefined : this.form.value.activitiesToAutomate,
            reportConfig: this.form.value.generateReport ? this.reportConfig : undefined,
            autoAssociateFindingsWithAudit: this.form.value.associateFindingsWithAudit,
            autoSetEvaluationStatusForAssessmentTasks: this.form.value.autoSetEvaluationStatusForAssessmentTasks,
            executionStatusForAutomatedActivities: this.form.value.setExecutionStatusForAutomatedActivities
                ? this.form.value.executionStatusForAutomatedActivities
                : undefined,
            labelsForAutomatedActivities: this.form.value.setLabelsForAutomatedActivities
                ? this.form.value.labelsForAutomatedActivities
                : undefined,
            labelsForAutomatedFindings: this.form.value.setLabelsForAutomatedFindings
                ? this.form.value.labelsForAutomatedFindings
                : undefined,
            factRequestParamsOverrides: {
                [`artifact:${WellKnownArtifactTypes.ContentAnalysis}`]: {
                    language: this.form.value.contentAnalysisLanguage,
                    enableAiAnalysis: this.form.value.enableAiAnalysis,
                },
            },
            automationPolicy: this.mergeAutomationPolicies(
                this.form.value.webAutomationPolicy,
                this.form.value.mobileAutomationPolicy
            ),
        };

        this.auditService.startAutomation(this.data.auditId, automationConfig).subscribe({
            next: (run: AuditAutomationRunId) => {
                this.webmateNotificationService.toastNotification('Automation started successfully');
                if (this.data.createCallback) {
                    this.data.createCallback(run);
                }
                this.starting = false;
                this.toolService.closeLastTool();
            },
            error: (error) => {
                console.error('Error starting automation:', error);
                this.starting = false;
                this.webmateNotificationService.toastErrorNotification('Failed to start automation: ' + error.message);
            }
        });
    }

    private mergeAutomationPolicies(
        webPolicy?: AutomationPolicyConfig,
        mobilePolicy?: AutomationPolicyConfig
    ): AutomationPolicyConfig | undefined {
        const presetSpecs = [...(webPolicy?.presetSpecs || []), ...(mobilePolicy?.presetSpecs || [])];
        const policySpecs = [...(webPolicy?.policySpecs || []), ...(mobilePolicy?.policySpecs || [])];
        return presetSpecs.length > 0 || policySpecs.length > 0 ? {presetSpecs, policySpecs} : undefined;
    }

    configureReport() {
        this.toolService.openTool(CreateAuditReportToolComponent, new CreateAuditReportToolConfig(
            'Configure Report',
            this.data.auditId,
            this.data.projectId,
            true,
            this.data.auditReportParameters,
            (reportConfig, templateId) => {
                this.reportConfig = reportConfig;
                this.reportTemplate = this.allReportTemplates.find(template => template.id === templateId) || this.reportTemplate;
                this.toolService.closeLastTool();
            },
            'Confirm Report Configuration'
        ));
    }

    openPlanningForSelection() {
        this.toolService.openTool(PlanningMatrixSelectionToolComponent, new PlanningMatrixToolConfig(
            'Select Activities for Automation',
            this.data.projectId,
            this.data.auditId,
            true,
            this.selectedActivities,
            false,
            (selectedActivities: { [specId: string]: HydratedAuditTarget[] }) => {
                this.plannedCustomObjects = [];
                this.selectedActivities = Object.keys(selectedActivities).reduce((accumulator, specId) => {
                    const objects = selectedActivities[specId];

                    objects.forEach(object => {
                        if (object.target.type === 'TagObservationTarget') {
                            this.plannedCustomObjects.push(object.target.name);
                        }
                    });

                    const filtered = objects
                        .filter(object => object.target.type !== 'TagObservationTarget')
                        .map(object => AuditService.generateFlyweightActivityId(object.testObjectId, specId));

                    return accumulator.concat(filtered);
                }, [] as AuditActivityId[]);

                this.form.patchValue({activitiesToAutomate: this.selectedActivities});
                this.toolService.closeLastTool();
            }
        ));
    }

    protected readonly faSyncAlt = faSyncAlt;
    protected readonly faExclamationCircle = faExclamationCircle;
}
